export const meta = {
  id: 2,
  title: 'How Functional Components Render Without a render() Method',
  excerpt: 'Exploring the lifecycle of a functional component. How React hooks tie into the Fiber architecture to manage state and side effects.',
  date: 'February 12, 2024',
  category: 'React',
  tags: ['React', 'JavaScript', 'Hooks'],
  readingTime: '6 min read',
  difficulty: 'Intermediate',
  isPublished: true
};

Class components rely on object instances and explicit lifecycle methods. But how do simple JavaScript functions hold state across renders?

## Closures and the Hooks Array

When a functional component runs, React attaches a linked list of hook states to the component's Fiber node. The order in which hooks are called determines which state maps to which variable.

<Callout type="warning">
This is precisely why you cannot call hooks conditionally, doing so would break the pointer order in the Fiber node during the next render phase.
</Callout>

```javascript
function MyComponent() {
  // React knows this is the first hook for this Fiber
  const [count, setCount] = useState(0); 
  
  // React knows this is the second hook
  useEffect(() => {
    document.title = \`Count: \${count}\`;
  }, [count]);
  
  return <div>{count}</div>;
}
```

Behind the scenes, React maintains a `currentlyRenderingFiber` reference. When `useState` is called, React looks at the `memoizedState` of the current Fiber, reads the correct value from the linked list, and returns it.
